<template>
  <nav>
    <ul>
      <li>
        <transition
          enter-active-class="transform transition duration-500 ease-in-out"
          leave-active-class="transform transition duration-500 ease-in-out"
          enter-from-class="-translate-x-full md:translate-x-0 md:opacity-0"
          enter-to-class="translate-x-0 md:translate-x-0 md:opacity-100"
          leave-from-class="translate-x-0 md:opacity-100"
          leave-to-class="-translate-x-full md:translate-x-0 md:opacity-0"
        >
          <SfDrawer
            ref="headerRef"
            disable-click-away
            placement="top"
            :model-value="isOpen"
            class="z-10 mr-[50px] flex max-h-screen w-full overflow-y-auto bg-white p-0 shadow-lg outline-0 md:!absolute md:!top-28 md:mr-0"
          >
            <div class="mx-auto flex w-full max-w-screen-xl gap-2 pt-3">
              <div
                class="mx-auto grid basis-3/4 gap-2"
                :style="{ 'grid-template-columns': `repeat(${Math.min(categories.length, 4)}, minmax(0, 1fr)` }"
              >
                <ul
                  v-for="({ name, slug, childrenItems }, categoryIndex) in categories"
                  ref="category"
                  :key="name"
                  tabindex="-1"
                  class="mb-6"
                >
                  <SfListItem
                    tag="a"
                    size="sm"
                    class="py-4 font-bold md:py-3"
                    tabindex="0"
                    @click="routeToDomain(generateLink(slug, undefined))"
                    @keydown.enter="routeToDomain(generateLink(slug, undefined))"
                    @keydown.arrow-down.prevent="focusNext()"
                    @keydown.arrow-up.prevent="focusPrevious(categoryIndex)"
                    @keydown.arrow-right.prevent="focusNextIndexElement((childrenItems?.length ?? 0) + 1)"
                    @keydown.arrow-left.prevent="focusPreviousIndexElement((categories[categoryIndex - 1]?.childrenItems?.length ?? 0) + 1)"
                    @keydown.tab.prevent="focusCategory"
                  >
                    {{ name }}
                  </SfListItem>
                  <ul>
                    <li
                      v-for="(item, childIndex) in childrenItems"
                      :key="item.name"
                      tabindex="-1"
                    >
                      <SfListItem
                        tag="a"
                        size="sm"
                        class="py-4 text-neutral-700 md:py-1"
                        tabindex="0"
                        @click="routeToDomain(generateLink(item.slug, undefined))"
                        @keydown.enter="routeToDomain(generateLink(item.slug, undefined))"
                        @keydown.arrow-down.prevent="focusNext()"
                        @keydown.arrow-up.prevent="focusPrevious(categoryIndex, childIndex)"
                        @keydown.arrow-right.prevent="focusRightElement(categoryIndex, childIndex)"
                        @keydown.arrow-left.prevent="focusLeftElement(categoryIndex, childIndex)"
                        @keydown.tab.prevent="focusCategory"
                      >
                        {{ item.name }}
                      </SfListItem>
                    </li>
                  </ul>
                </ul>
              </div>
              <div class="mr-2 flex grow basis-1/4 flex-col items-start lg:mr-xl lg:max-w-80">
                <SfListItem
                  tag="a"
                  size="sm"
                  class="py-4 text-primary-700 md:py-1.5"
                  tabindex="0"
                  @click="routeToDomain(generateLink(undefined, 'new'))"
                  @keydown.enter="routeToDomain(generateLink(undefined, 'new'))"
                  @keydown.arrow-down.prevent="focusNext()"
                  @keydown.arrow-up.prevent="focusPrevious"
                  @keydown.tab.prevent="focusCategory"
                >
                  {{ $t('New') }}
                </SfListItem>
                <SfListItem
                  tag="a"
                  size="sm"
                  class="py-4 text-primary-700 md:py-1.5"
                  tabindex="0"
                  @click="routeToDomain(generateLink(undefined, 'sale'))"
                  @keydown.enter="routeToDomain(generateLink(undefined, 'sale'))"
                  @keydown.arrow-down.prevent="focusNext()"
                  @keydown.arrow-up.prevent="focusPrevious"
                  @keydown.tab.prevent="focusCategory"
                >
                  {{ $t('Sale') }}
                </SfListItem>
                <SfListItem
                  tag="a"
                  size="sm"
                  class="py-4 text-primary-700 md:py-1.5"
                  tabindex="0"
                  @click="routeToDomain(`/c/${props.categorySlug}?filters=mykik`)"
                  @keydown.enter="routeToDomain(generateLink(undefined, undefined))"
                  @keydown.arrow-down.prevent="focusNext(images.length === 0)"
                  @keydown.arrow-up.prevent="focusPrevious"
                  @keydown.tab.prevent="focusCategory"
                >
                  {{ $t("myKiK") }}
                </SfListItem>
                <div
                  v-for="(image, index) in images"
                  :key="image.alt"
                  class="mt-2 w-full max-w-64 border-neutral-300 bg-neutral-100 last-of-type:mb-4 md:rounded-md"
                >
                  <SharedLink
                    :link="image.to"
                    @keydown.arrow-down.prevent="focusNext(index === images.length - 1)"
                    @keydown.arrow-up.prevent="focusPrevious"
                    @keydown.tab.prevent="focusCategory"
                  >
                    <NuxtImg
                      :data-type="image.to.type"
                      :alt="image.alt"
                      :src="image.url"
                      :srcset="image.srcSet"
                      loading="lazy"
                      class="max-h-52 w-full object-contain"
                    />
                  </SharedLink>
                  <p class="my-2 px-4 text-center font-medium">
                    {{ image.to.name }}
                  </p>
                </div>
              </div>
            </div>
          </SfDrawer>
        </transition>
      </li>
    </ul>
  </nav>
</template>

<script lang="ts" setup>
import {
  SfDrawer,
  SfListItem,
} from '@storefront-ui/vue';
import { urlGetters } from '~/composables/getters';

interface Props {
  isOpen?: boolean;
  categorySlug: string;
  categories?: NavbarCategory[];
  images?: any[];
}

const { focusNextElement, focusPreviousElement, focusNextIndexElement, focusPreviousIndexElement } = useFocus();

const emit = defineEmits(['close', 'focusNextHeaderNavigation', 'focusCurrentHeaderNavigation', 'focusPreviousHeaderNavigation']);

const category = ref<HTMLElement[] | null>(null);

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  categories: () => [],
  images: () => [],
});

const focusNext = (isLast?: boolean) => {
  if (isLast) {
    emit('focusNextHeaderNavigation');

    return;
  }

  focusNextElement();
};

const focusPrevious = (categoryIndex: number, childIndex?: number) => {
  if (categoryIndex === 0 && childIndex === undefined) {
    emit('focusCurrentHeaderNavigation');

    return;
  }

  focusPreviousElement();
};

const focusLeftElement = (categoryIndex: number, childIndex: number) => {
  if (categoryIndex === 0) {
    emit('focusCurrentHeaderNavigation');

    return;
  }

  if (childIndex >= (props.categories[categoryIndex - 1].childrenItems?.length ?? 0)) {
    const lastElementOfPreviousCategory = childIndex + 2;

    focusPreviousIndexElement(lastElementOfPreviousCategory);

    return;
  }

  const previousFocusIndex = (props.categories[categoryIndex - 1].childrenItems?.length ?? 0) + 1;

  focusPreviousIndexElement(previousFocusIndex);
};

const focusRightElement = (categoryIndex: number, childIndex: number) => {
  if (categoryIndex + 1 >= props.categories.length) {
    focusNextElement();

    return;
  }

  if (childIndex >= (props.categories[categoryIndex + 1].childrenItems?.length ?? 0)) {
    const firstElementOfNextCategory = (props.categories[categoryIndex].childrenItems?.length ?? 0) - childIndex;

    focusNextIndexElement(firstElementOfNextCategory);

    return;
  }

  const nextFocusIndex = (props.categories[categoryIndex].childrenItems?.length ?? 0) + 1;
  focusNextIndexElement(nextFocusIndex);
};

const routeToDomain = (link: string) => {
  emit('close');
  navigateTo(link);
};

const focusCategory = (event: KeyboardEvent) => {
  emit(event.shiftKey ? 'focusPreviousHeaderNavigation' : 'focusNextHeaderNavigation');
};

const { $i18n } = useNuxtApp();

const generateLink = (CategorySlug?: string, saleOrNew?: 'sale' | 'new') => {
  return `${urlGetters.getCategoryNavigationByType(saleOrNew, $i18n)}/${props.categorySlug}${CategorySlug ? `/${CategorySlug}` : ''}`;
};
</script>
